import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPalette, type PaletteItem, type PaletteSource } from '@beyond-js/ui/react';
import { useApi } from '@cdn/api';
import { useFamily } from '@cdn/family';
import { useI18n } from '@cdn/i18n';
import { useSession } from '@cdn/identity';
import type { Paths } from '@cdn/navigation';

/**
 * ⌘K / Ctrl+K: the family's `CommandPalette` as CDN's navigator (decision E7 with ui 0.16.0, DQ-21): the
 * organization's applications by name (`applications.list` with `q`, a few at a time), CDN's sections and the
 * actions a person may take, with the items opened last on this device. A chosen item is routed in place.
 */
export function Palette({ paths }: { paths: Paths }) {
	const { t, locale } = useI18n();
	const api = useApi();
	const session = useSession();
	const family = useFamily();
	const navigate = useNavigate();
	const labels = CommandPalette.labels[locale.code === 'es' ? 'es' : 'en'];
	const organization = session.organization?.organization;
	const manage = session.can('application.manage');
	const providers = session.can('providers.manage');

	const sources = useMemo<PaletteSource[]>(() => {
		const match = (query: string, items: PaletteItem[]) => {
			const text = query.trim().toLowerCase();
			return text ? items.filter(item => item.label.toLowerCase().includes(text)) : items;
		};
		const sections: PaletteItem[] = [
			{ id: 'applications', label: t('shell.nav.applications'), href: paths.applications },
			...(family.standalone ? [] : [{ id: 'projects', label: t('shell.nav.projects'), href: paths.projects }]),
			{ id: 'consumption', label: t('shell.nav.consumption'), href: paths.consumption },
			{ id: 'plan', label: t('shell.nav.plan'), href: paths.plan },
			...(providers ? [{ id: 'providers', label: t('shell.nav.providers'), href: paths.providers }] : [])
		];
		const actions: PaletteItem[] = manage ? [{ id: 'register', label: t('applications.create.open'), href: paths.create }] : [];
		return [
			{
				key: 'applications',
				label: t('shell.palette.applications'),
				empty: true,
				search: async (query, { signal }) => {
					if (!organization) return [];
					const page = await api.call('applications.list', { params: { organization }, query: { limit: 8, ...(query.trim() ? { q: query.trim() } : {}) }, signal });
					return page.items.map(application => ({ id: application.id, label: application.title ?? application.name, detail: application.name, href: paths.application(application.id) }));
				}
			},
			{ key: 'sections', label: t('shell.palette.sections'), empty: true, search: query => match(query, sections) },
			{ key: 'actions', label: t('shell.palette.actions'), empty: true, search: query => match(query, actions) }
		];
	}, [api, organization, paths, family.standalone, manage, providers, t]);

	return (
		<CommandPalette
			sources={sources}
			recent={{ key: 'cdn' }}
			shortcut
			labels={labels}
			onNavigate={(item, event) => {
				if (!item.href) return;
				event.preventDefault();
				const url = new URL(item.href, window.location.origin);
				navigate(`${url.pathname}${url.search}${url.hash}`);
			}}
		/>
	);
}
